import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import type { BatchReleaseMode } from '@ooh/contracts';
import {
  ReleaseModeChoice,
  type ReleaseModeChoiceLabels,
} from '../../reservations/components/unified-select-release-mode-choice';

/** Shared inline confirm-step shell for Sprzedaj and Wymuś zwolnienie — both require an
 * explicit confirmation before their mutation fires, differing only in body text and
 * confirm-button color. `namePrefix` (e.g. `${testId}-close-sale`) derives the wrapper
 * and button test ids, matching each action's pre-existing id scheme. */
export function InspectActionConfirmStep({
  namePrefix,
  body,
  confirmVariant,
  onCancel,
  onConfirm,
}: {
  namePrefix: string;
  body: string;
  confirmVariant: 'success' | 'destructive';
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('reservations');
  return (
    <div className="flex flex-col gap-1.5 w-full" data-testid={`${namePrefix}-confirm`}>
      <p className="text-xs text-muted-foreground">{body}</p>
      <div className="flex gap-1.5">
        <Button
          size="xs"
          variant="outline"
          data-testid={`${namePrefix}-cancel-btn`}
          onClick={onCancel}
        >
          {t('dialogs.cancelBtnLabel')}
        </Button>
        <Button
          size="xs"
          variant={confirmVariant}
          data-testid={`${namePrefix}-confirm-btn`}
          onClick={onConfirm}
        >
          {t('dialogs.confirmLabel')}
        </Button>
      </div>
    </div>
  );
}

/** Context for the Anuluj/Wymuś whole-versus-selected-period release choice
 * (CU-86cbh03pj INSPECT-popover parity) — present only when the inspected cell's date
 * under-covers the reservation's own full range (`resolveOccupiedCellView`'s
 * `isReleaseCellUnderCovered`). */
export interface ReleaseChoiceContext {
  mediaUnitId: number;
  reservationId: number;
  cellDateFrom: string;
  cellDateTo: string;
}

/** Shared Anuluj/Wymuś zwolnienie confirm step ONCE a `ReleaseChoiceContext` is present —
 * adds `ReleaseModeChoice` atop the existing body and blocks confirming until a mode is
 * picked (mirrors `UnifiedSelectPopover`'s `resolveReleaseChoicePending`). */
export function InspectReleaseChoiceConfirmStep({
  namePrefix,
  body,
  confirmVariant,
  mode,
  onModeChange,
  labels,
  pending,
  onCancel,
  onConfirm,
}: {
  namePrefix: string;
  body?: string;
  confirmVariant: 'success' | 'destructive';
  mode: BatchReleaseMode | null;
  onModeChange: (mode: BatchReleaseMode) => void;
  labels: ReleaseModeChoiceLabels;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('reservations');
  return (
    <div className="flex flex-col gap-1.5 w-full" data-testid={`${namePrefix}-confirm`}>
      {body ? <p className="text-xs text-muted-foreground">{body}</p> : null}
      <ReleaseModeChoice value={mode} onChange={onModeChange} disabled={pending} labels={labels} />
      <div className="flex gap-1.5">
        <Button
          size="xs"
          variant="outline"
          data-testid={`${namePrefix}-cancel-btn`}
          onClick={onCancel}
        >
          {t('dialogs.cancelBtnLabel')}
        </Button>
        <Button
          size="xs"
          variant={confirmVariant}
          disabled={mode === null || pending}
          data-testid={`${namePrefix}-confirm-btn`}
          onClick={onConfirm}
        >
          {t('dialogs.confirmLabel')}
        </Button>
      </div>
    </div>
  );
}
